import {
    ColumnApi,
    ColumnEvent,
    GridApi,
    RowNode,
} from '@ag-grid-enterprise/all-modules';
import DateComparator from '_legacy/components/grid/comparators/DateComparator';
import PriorityListComparator from '_legacy/components/grid/comparators/PriorityListComparator';
import {
    DATASET_AFRAMAX_LONDON,
    DATASET_CLEAN_LONDON,
    DATASET_FUEL_OIL_LONDON,
    DATASET_SUEZMAX_HOUSTON,
    DATASET_SUEZMAX_LONDON,
} from '_legacy/models/Datasets';
import CategoryComparator from '_legacy/modules/columns/vessel/comparators/CategoryComparator';
import _ from 'lodash';
import { BaseGridConfig } from 'store/feature/layoutsSlice/models';
import { firstBy } from 'thenby';
import { GridComponent } from './models';
import { getUserId } from '../../../_legacy/reducers/userSelectors';
import store from 'store';
import { loadZones, specialOrderGroups } from '_legacy/models/Location';

export module GridUtils {
    export function initializeGridColumns(
        columnApi: ColumnApi,
        userColumnOptions: BaseGridConfig['columnOptions']
    ): void {
        if (columnApi) {
            const columns = columnApi.getAllColumns() ?? [];
            //show columns which cannot be hidden, in case they were previously and had been stored in column state
            const columnsCannotBeHidden = columns.filter(
                (c) =>
                    c.getColDef().suppressColumnsToolPanel &&
                    c.getColDef().suppressMovable
            );

            columnApi.setColumnsVisible(
                columnsCannotBeHidden.map((c) => c.getColId()),
                true
            );

            const existingColIds = (userColumnOptions || []).map(
                (c) => c.colId
            );

            //find columns that have a width set but aren't contained in initial columns
            //as we don't want to override widths the user has already defined
            const columnsWithWidths = columns.filter(
                (c) =>
                    c.getColDef().width &&
                    existingColIds.indexOf(c.getColId()) === -1
            );

            //set widths to those originally specified
            for (let index = 0; index < columnsWithWidths.length; index++) {
                const col = columnsWithWidths[index];
                //override anything that has been restored
                columnApi.setColumnWidth(
                    col.getColId(),
                    col.getColDef().width ?? 100
                );
            }
        }
    }

    export function updateTimestamp(api: GridApi): void {
        _.set(api, '__timestamp', new Date().getTime());
    }

    // Cache parsed createdDateTime values to avoid repeated new Date() calls
    // across 250K rows on every filter pass.
    const _createdTsCache = new WeakMap<object, number>();

    export function getCreatedTs(data: Record<string, any>): number {
        let ts = _createdTsCache.get(data);
        if (ts === undefined) {
            const raw = data.createdDateTime;
            ts = raw ? new Date(raw).getTime() : -Infinity;
            _createdTsCache.set(data, ts);
        }
        return ts;
    }

    export function isLaterThanTimestamp(
        api: GridApi,
        input: string | Date | undefined
    ): boolean {
        // Direct property access is faster than _.get for a hot path called
        // 250K times per filter cycle.
        const timestamp: number =
            (api as any).__timestamp ?? new Date().getTime();
        if (typeof input === 'string') {
            return new Date(input).getTime() > timestamp;
        } else if (input instanceof Date) {
            return input.getTime() > timestamp;
        } else {
            return false;
        }
    }

    export function wrapFilterInstances(api: GridApi): void {
        const filterModel = api.getFilterModel();
        const userId = getUserId(store.getState());
        _(filterModel)
            .keys()
            .forEach((colId) => {
                const fInstance: Record<string, any> | undefined =
                    api.getFilterInstance(colId)!;
                if (fInstance?.filters) {
                    fInstance.filters.forEach((f) =>
                        wrapFilterInstance(f, userId)
                    );
                } else if (fInstance) {
                    wrapFilterInstance(fInstance, userId);
                }
            });
    }

    function wrapFilterInstance(
        fInstance: Record<string, any>,
        userId: string
    ): void {
        if (!fInstance.__doesFilterPass) {
            fInstance.__doesFilterPass = fInstance.doesFilterPass;
            fInstance.doesFilterPass = (params) => {
                // Use cached numeric timestamp to avoid new Date() parse on
                // every row — this runs 250K times per filter cycle.
                const gridTs: number =
                    (params.node.gridApi as any).__timestamp ??
                    new Date().getTime();
                if (
                    getCreatedTs(params.data) > gridTs &&
                    params.data.createdBy?.userId === userId
                ) {
                    return true;
                }
                return fInstance.__doesFilterPass(params);
            };
        }
    }

    export function reloadRowGroupingIndentation(
        newThemeEnabled: boolean,
        columnApi: ColumnApi
    ): void {
        const currentColumnModel = columnApi.getColumnState();
        const isSelectedColumn = currentColumnModel.find(
            (column) => column.colId === 'isSelected'
        );

        const rowGroupCount = currentColumnModel.filter(
            (column) => column.rowGroup
        ).length;
        if (isSelectedColumn) {
            if (newThemeEnabled) {
                const width = rowGroupCount > 1 ? 77 : 35;
                isSelectedColumn.width = width;
                (isSelectedColumn as any).minWidth = width;

                columnApi.setColumnState(currentColumnModel);
            }
        }
    }

    export function columnsPostSort(
        rowNodes: RowNode[],
        sortingIsActive: boolean,
        datasetId: number
    ): void {
        if (rowNodes.length === 0) return;

        //if sorting has been applied by the user,
        //other mechanisms ensure the sorting is consistent via created date time comparators
        if (!sortingIsActive) {
            //if no sorting has been applied by the user, we should reorder the rows to be ordered by created date time, load zone and
            //reported date descending leaving the most recently inserted records data at the top of the grid, as expected by the users.
            // groupId and zonePriorityList are captured once at sort-call time
            // (a consistent snapshot) rather than re-reading the store on every
            // comparison — both intentional and more correct.
            const groupId = store.getState().user?.group?.id;
            const useZonePrioritySort =
                datasetId === DATASET_SUEZMAX_LONDON ||
                datasetId === DATASET_SUEZMAX_HOUSTON ||
                datasetId === DATASET_AFRAMAX_LONDON ||
                datasetId === DATASET_FUEL_OIL_LONDON ||
                specialOrderGroups.includes(groupId ?? '');
            const zonePriorityList = useZonePrioritySort
                ? loadZones(datasetId, groupId ?? '')
                : [];

            rowNodes.sort(
                firstBy<RowNode>((n1, n2) => {
                    if (n1.data && n2.data) {
                        return DateComparator(
                            n1.data.reportedDate,
                            n2.data.reportedDate
                        );
                    }
                    return 0;
                }, -1)
                    .thenBy<RowNode>((n1, n2) => {
                        if (useZonePrioritySort) {
                            if (n1.data && n2.data) {
                                const a = n1.data.loadZone;
                                const b = n2.data.loadZone;
                                const aIsBlank =
                                    !a || (Array.isArray(a) && a.length === 0);
                                const bIsBlank =
                                    !b || (Array.isArray(b) && b.length === 0);
                                if (aIsBlank !== bIsBlank)
                                    return aIsBlank ? 1 : -1;
                                return PriorityListComparator(
                                    a ?? '',
                                    b ?? '',
                                    zonePriorityList
                                );
                            }
                        } else if (datasetId === DATASET_CLEAN_LONDON) {
                            if (n1.data && n2.data) {
                                return CategoryComparator(
                                    n1.data.vesselCategory,
                                    n2.data.vesselCategory,
                                    n1,
                                    n2
                                );
                            }
                        }

                        return 0;
                    })
                    .thenBy<RowNode>((n1, n2) => {
                        if (n1.data && n2.data) {
                            return DateComparator(
                                n1.data.createdDateTime,
                                n2.data.createdDateTime
                            );
                        }
                        return 0;
                    }, -1)
            );
        }

        // Two-pass O(n) split instead of splice-in-loop O(n²):
        // collect session nodes and remaining nodes in a single pass, then
        // rebuild rowNodes in-place without repeated array re-indexing.
        const sessionNodes: RowNode[] = [];
        const remaining: RowNode[] = [];
        for (const node of rowNodes) {
            const { data, gridApi } = node as any;
            if (
                data &&
                getCreatedTs(data) >
                    ((gridApi as any).__timestamp ?? new Date().getTime())
            ) {
                sessionNodes.push(node);
            } else {
                remaining.push(node);
            }
        }
        if (sessionNodes.length > 0) {
            sessionNodes.sort((n1, n2) => {
                if (n1.data && n2.data) {
                    return -DateComparator(
                        n1.data.createdDateTime,
                        n2.data.createdDateTime
                    );
                }
                return 0;
            });
            rowNodes.length = 0;
            for (const node of sessionNodes) rowNodes.push(node);
            for (const node of remaining) rowNodes.push(node);
        }
    }

    export function updateRowGroupsIfNeeded(
        { columnApi }: Pick<GridComponent, 'api' | 'columnApi'>,
        rowGroupIds: string[]
    ): void {
        const currentRowGroups = columnApi
            .getRowGroupColumns()
            .map((c) => c.getColId());
        if (!_.isEqual(currentRowGroups, rowGroupIds)) {
            columnApi.setRowGroupColumns(rowGroupIds);
        }
    }

    export function isColumnEventEligible(e: ColumnEvent): boolean {
        const source = e.source?.toLowerCase() ?? '';
        return (
            e.type === 'sortChanged' ||
            source.startsWith('ui') ||
            source.endsWith('ui')
        );
    }

    function collapseRowGroup(api: any, groupPath): void {
        const rootNode = api.rowModel.rootNode;
        const groupLevels = groupPath.split('_');
        groupLevels.shift(); // 'root' at the begginig is not needed

        let node = rootNode;
        groupLevels.forEach(
            (groupLevel) =>
                (node =
                    node && groupLevel in node.childrenMapped
                        ? node.childrenMapped[`${groupLevel}`]
                        : null)
        );

        if (node && node.expanded) {
            node.setExpanded(false);
        }
    }

    export function updateCollapsedRowGroups(
        { api }: Pick<GridComponent, 'api'>,
        collapsedRowGroups: string[]
    ): void {
        api.expandAll();
        collapsedRowGroups.forEach((groupPath) =>
            collapseRowGroup(api, groupPath)
        );
    }
}
